import { Loader2 } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import {
  apiFetch,
  formatApiError,
  useAuthStatus,
  useSavedLocations,
} from '../shared/api/client'
import type { PaginatedResponse, SavedLocation } from '../shared/api/types'

export function SaveLocationButton({ locationId }: { locationId: string }) {
  const queryClient = useQueryClient()
  const authStatus = useAuthStatus()
  const savedLocations = useSavedLocations(Boolean(authStatus.data?.authenticated))
  const savedEntry = savedLocations.data?.results.find((entry) => entry.location.id === locationId)

  const mutation = useMutation({
    mutationFn: async () => {
      if (!authStatus.data?.authenticated) {
        throw { message: 'Sign in to save locations.' }
      }

      if (savedEntry) {
        await apiFetch(`/saved-locations/${locationId}`, { method: 'DELETE' })
        return { action: 'delete' as const }
      }

      const created = await apiFetch<SavedLocation>('/saved-locations', {
        method: 'POST',
        body: JSON.stringify({ location: locationId }),
      })
      return { action: 'create' as const, created }
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['saved-locations'] })
      const previous = queryClient.getQueryData<PaginatedResponse<SavedLocation>>(['saved-locations'])

      if (!previous) {
        return { previous }
      }

      if (savedEntry) {
        queryClient.setQueryData<PaginatedResponse<SavedLocation>>(['saved-locations'], {
          ...previous,
          count: Math.max(previous.count - 1, 0),
          results: previous.results.filter((entry) => entry.location.id !== locationId),
        })
      }

      return { previous }
    },
    onSuccess: async (result) => {
      queryClient.setQueryData<PaginatedResponse<SavedLocation> | undefined>(
        ['saved-locations'],
        (current) => {
          if (!current) {
            if (result.action === 'create') {
              return {
                count: 1,
                next: null,
                previous: null,
                results: [result.created],
              }
            }
            return current
          }

          if (result.action === 'delete') {
            return current
          }

          const alreadyExists = current.results.some(
            (entry) => entry.location.id === result.created.location.id,
          )
          if (alreadyExists) {
            return current
          }

          return {
            ...current,
            count: current.count + 1,
            results: [result.created, ...current.results],
          }
        },
      )

      await queryClient.invalidateQueries({ queryKey: ['saved-locations'] })
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['saved-locations'], context.previous)
      }
    },
  })

  const visibleCount = savedLocations.data?.count ?? 0

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending || savedLocations.isLoading}
        className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition ${
          savedEntry
            ? 'bg-slate-900 text-white hover:bg-slate-800'
            : 'bg-emerald-700 text-white hover:bg-emerald-800'
        } disabled:cursor-not-allowed disabled:opacity-60`}
      >
        {mutation.isPending ? <Loader2 size={16} className="animate-spin" /> : null}
        <span>{savedEntry ? 'Saved ✓' : 'Save location'}</span>
      </button>

      {mutation.isError ? (
        <p className="text-sm text-rose-600">{formatApiError(mutation.error)}</p>
      ) : null}

      {authStatus.data?.authenticated && (savedLocations.data || mutation.isPending) ? (
        <p className="text-sm text-slate-500">Saved places: {visibleCount}</p>
      ) : null}
    </div>
  )
}
